import React, { FC, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { ICaremapNote } from '../../notes/model/NotesClient';
import { IReactionBtnCodes, ReactionButton } from './ReactionButton';
import { IonPopover } from '@ionic/react';
import { isPlatform } from '../../utils/isPlatform';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { useIfLoggedIn } from '../../utils/useIfLoggedIn';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { AlertPresenter } from '../../AlertBinder';

const useStyles = createUseStyles({
    reactions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',

        '& > div': {
            marginRight: 12,
            display: 'inline-block'
        }
    },

    popover: {
        '--width': 'fit-content',

        '&::part(content)': {
            maxWidth: '95%'
        }
    },

    popoverContent: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: '2px 4px 0',
        justifyContent: 'flex-start'
    }
});

export type INoteReactions = {
    post: ICaremapNote;
    onReact?: (reaction: string) => void;
};

const isDesktop = isPlatform('desktop');
export const Reactions: FC<INoteReactions> = ({
    post,
    onReact = () => {}
}) => {
    const classes = useStyles();

    const alert = useInstance(AlertPresenter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const mixpanel = useInstance(MixPanelEventEmitter);
    const activeCareMap = careMapState.getActiveCaremap();
    const notesModel = useInstance(CaremapNotesModelFactory).forActiveCaremap();
    const reactionsModel = notesModel.reactions;

    const popover = useRef<HTMLIonPopoverElement>(null);
    const [expanded, setExpanded] = useState<boolean>(false);

    const ifLoggedIn = useIfLoggedIn();

    const signInMsg = 'Please sign in or create an account to add reactions.';
    const onReactionClicked = (reaction: IReactionBtnCodes) => {
        ifLoggedIn({
            delegate: () => {
                const isAdd = reaction === 'add';

                // otherwise typescript complains
                if (!isAdd) {
                    reactionsModel.toggleReaction({ note: post, reaction }).catch((e: any) => {
                        if (e.status === 404) {
                            notesModel.localDeleteNote(post);
                            alert.showAlertV2({
                                header: 'Deleted Post',
                                message: `We're sorry, the post you reacted to has been deleted.`
                            });
                        } else {
                            alert.showAndLogError(e);
                        }
                    });
                }

                onReact(reaction);

                if (isDesktop) {
                    popover.current?.dismiss();
                } else {
                    setExpanded(false);
                }

                if (!isAdd && !reactionsModel.hasUserReacted({ note: post, reaction })) {
                    mixpanel.track('Reaction Added', () => ({
                        'Reaction': reaction
                    }));
                }
            },
            message: signInMsg,
            redirect: `/community/${activeCareMap?.careMapId}/feed`,
            trackingFrom: 'Reactions'
        });
    };

    const onAddReaction = async (e: React.MouseEvent) => {
        ifLoggedIn({
            delegate: () => {
                popover.current!.event = e;
                setExpanded(true);
            },
            message: signInMsg,
            redirect: `/community/${activeCareMap?.careMapId}/feed`,
            trackingFrom: 'Reactions'
        });
    };

    const possibleReactions = reactionsModel.getSupportedReactions();
    const hasLoveReaction = reactionsModel.getNames({ note: post, reaction: 'love' }).length;

    return (
        <>
            <div className={classes.reactions}>
                {!hasLoveReaction && (
                    <ReactionButton
                        reaction="love"
                        onClick={(reactionCode) => onReactionClicked(reactionCode)}
                        filled
                    />
                )}
                {possibleReactions.map(reaction => {
                    const count = reactionsModel.getNames({ note: post, reaction }).length;
                    const userHasReacted = reactionsModel.hasUserReacted({ note: post, reaction });
                    return count ? (
                        <ReactionButton
                            hasReaction
                            count={count}
                            key={reaction}
                            reaction={reaction}
                            userHasReacted={userHasReacted}
                            onClick={(reactionCode) => onReactionClicked(reactionCode)}
                            filled
                        />
                    ) : null;
                })}
                <ReactionButton
                    reaction="add"
                    onClick={(code, e) => onAddReaction(e)}
                    filled
                />
            </div>

            <IonPopover
                side="top"
                alignment="center"
                ref={popover}
                isOpen={expanded}
                showBackdrop={false}
                className={classes.popover}
                data-type="reactions-popover"
                onDidDismiss={() => setExpanded(false)}

                // need to set keepContentsMounted, otherwise content that expands popover
                // beyond minWidth sometimes pushes it off the right side of the screen
                // (see https://github.com/ionic-team/ionic-framework/issues/24716)
                keepContentsMounted
            >
                <div className={classes.popoverContent}>
                    {possibleReactions.map(reaction => (
                        <ReactionButton
                            large
                            useReactedIcon
                            key={reaction}
                            reaction={reaction}
                            onClick={(reactionCode) => onReactionClicked(reactionCode)}
                        />
                    ))}
                </div>
            </IonPopover>
        </>
    );
};
